/* eslint-disable @reatom/unit-naming-rule */
import { atom, withAssign } from '@reatom/framework'

import { AsyncStateN, Result } from '@blade/std-lib'
import { networkDataNativePriceAtom } from '@root/entities/network/atoms'
import { userTradingAnalyticsPeriodAtom } from '@root/features/user/user-trading-analytics/atoms'
import { reatomResource } from '@root/shared/lib'

import { toTradingAnalytics, WalletProfile, windowOf } from '../model'

import { fetchWalletProfileRequestAction } from './wallet-profile-requests'

import type { Atom } from '@reatom/framework'
import type { Address } from '@root/entities/address/Address'
import type { Network } from '@root/entities/network'

/**
 * A person's own wallets, read as one portfolio.
 *
 * The profile screen was built against an endpoint that was never ported, so
 * every block on it showed an error or a permanent skeleton. This feeds the
 * same screen from the read that does exist — the one the wallet modal uses —
 * and the adapter between them was already written.
 *
 * The wallets arrive as an atom rather than a list because the selector above
 * the dashboard changes them: picking one wallet, or all of them, has to
 * refetch. The server adds them up; summing per-wallet answers on the client
 * would return one wallet's balance as the portfolio's, since a holding is
 * the last state each wallet reported rather than something that adds up.
 *
 * Nothing is asked for while there are no wallets: the resource holds its
 * "not initiated" state, which the screen reads as loading rather than as an
 * error nobody can act on.
 */
export const reatomWalletsTradingAnalytics = ({
  walletsAtom,
  network,
}: {
  walletsAtom: Atom<ReadonlyArray<Address>>
  network: Network.Network
}) => {
  const profileAtom = reatomResource(
    // Not an async body: the spies below have to be seen by the reaction that
    // runs this, and an async function hands control back before they are.
    (ctx) => {
      const wallets = ctx.spy(walletsAtom)
      const window = windowOf(ctx.spy(userTradingAnalyticsPeriodAtom))

      const [first] = wallets
      if (!first) {
        // No wallets, so nothing to ask about — and zero is the true answer
        // rather than a placeholder: an account with no wallet has traded
        // nothing. Holding a skeleton instead would be a page that never
        // resolves and never says why.
        return Promise.resolve(Result.succeed(WalletProfile.EMPTY))
      }

      return ctx.schedule(() =>
        fetchWalletProfileRequestAction(ctx, {
          network,
          walletAddress: first,
          walletAddresses: wallets,
          window,
        }),
      )
    },
    { name: 'reatomWalletsProfile', loadOnConnect: true },
  )

  const analyticsAtom = atom((ctx) => {
    // In dollars or in the native asset, whichever the reader picked; the
    // figures are computed in dollars and divided by this.
    const nativePrice = Number(ctx.spy(networkDataNativePriceAtom).value ?? 0)

    return AsyncStateN.map(ctx.spy(profileAtom), (profile) =>
      toTradingAnalytics(profile, network, nativePrice),
    )
  }, 'walletsAnalyticsAtom').pipe(
    withAssign(() => ({
      refresh: profileAtom.refresh,
      /**
       * The read as it arrived.
       *
       * The dashboard wants the mapped shape, because that is what its blocks
       * were written against. The portfolio screen wants the positions, the
       * entry market caps and the funding line, none of which survive the
       * mapping — so it reads the same request rather than making its own.
       */
      profile: profileAtom,
    })),
  )

  return analyticsAtom
}
